/**
 * Storage Service
 * Handles storage-related API calls
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { StorageAdapter } from '@/adapters';
import type { ApiResponse, StorageUrlResponseDto } from '@/types';

export const storageService = {
  /**
   * Generate presigned upload URL
   */
  async generateUploadUrl(fileName: string, contentType: string, metadata?: Record<string, unknown>) {
    // Backend expects: contentType in body, key as optional query param
    const requestBody = { contentType };

    // Generate a key if not provided in metadata
    const queryParams: Record<string, string> = {};
    if (metadata?.key && typeof metadata.key === 'string') {
      queryParams.key = metadata.key;
    } else {
      // Generate a key similar to what backend does
      queryParams.key = `uploads/${Date.now().toString()}_${Math.random().toString(36).substring(2, 15)}`;
    }

    // Build query string
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.STORAGE.UPLOAD_URL}?${queryString}` : API_ENDPOINTS.STORAGE.UPLOAD_URL;

    const response = await api.post<ApiResponse<StorageUrlResponseDto>>(endpoint, requestBody);
    const result = StorageAdapter.fromApi(response.data.data);

    // Validate the URL format and expiration
    if (!result.url || !result.url.includes('X-Amz-Signature')) {
      throw new Error('Invalid presigned URL received from server');
    }

    return result;
  },

  /**
   * Generate presigned download URL
   */
  async generateDownloadUrl(key: string) {
    const payload = StorageAdapter.toGenerateDownloadUrlRequest(key);
    const response = await api.post<ApiResponse<StorageUrlResponseDto>>(API_ENDPOINTS.STORAGE.DOWNLOAD_URL, payload);
    return StorageAdapter.fromApi(response.data.data);
  },

  /**
   * Generate presigned delete URL
   */
  async generateDeleteUrl(key: string) {
    const payload = StorageAdapter.toGenerateDeleteUrlRequest(key);
    const response = await api.post<ApiResponse<StorageUrlResponseDto>>(API_ENDPOINTS.STORAGE.DELETE_URL, payload);
    return StorageAdapter.fromApi(response.data.data);
  },

  /**
   * Delete file directly
   */
  async deleteFile(key: string) {
    const response = await api.delete(API_ENDPOINTS.STORAGE.DELETE(key));
    return response.data;
  },

  /**
   * Upload file using presigned URL
   */
  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    // Extract signed headers from URL to match exactly what was signed
    const url = new URL(uploadUrl);
    const signedHeadersParam = url.searchParams.get('X-Amz-SignedHeaders') || '';
    const signedHeaders = signedHeadersParam.split(';').filter(Boolean);

    console.log('Signed headers from URL:', signedHeaders);

    // Build headers object with ONLY the headers that were signed
    // Do NOT add any headers that weren't part of the signature
    const headers: Record<string, string> = {};

    // Only include Content-Type if it was explicitly signed
    if (signedHeaders.includes('content-type')) {
      headers['Content-Type'] = file.type;
    }

    // Note: Query parameters in the URL (like x-amz-acl, x-amz-checksum-crc32, etc.)
    // are NOT the same as headers and should NOT be added to the request headers.
    // The presigned URL already includes all necessary query parameters.

    // Convert File to Blob without type to prevent browser from auto-adding Content-Type header
    // This is crucial when content-type is not in the signed headers
    const bodyToSend = signedHeaders.includes('content-type')
      ? file // Send original file if content-type was signed
      : new Blob([file]); // Send as typeless Blob to avoid auto Content-Type header

    console.log('Uploading with headers:', headers, 'body type:', bodyToSend.constructor.name);

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: bodyToSend,
      headers
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => 'Unknown error');
      console.error('S3 upload failed:', {
        status: response.status,
        statusText: response.statusText,
        url: uploadUrl.substring(0, 100) + '...', // Truncate URL for security
        contentType: file.type,
        fileSize: file.size,
        fileName: file.name,
        errorBody
      });

      // Provide more specific error messages
      if (response.status === 403) {
        throw new Error(
          "Access denied to S3 bucket. The upload URL may have expired or the request headers don't match the signed URL. Please try again."
        );
      } else if (response.status === 400) {
        throw new Error('Bad request to S3. Check file format and size.');
      } else if (response.status === 413) {
        throw new Error('File too large. Please choose a smaller file.');
      } else {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
    }
  }
};
